<template>
  <div class="page-stack">
    <section class="section-shell section-shell--hero">
      <div class="section-head">
        <div>
          <div class="section-kicker">Integrations</div>
          <h3 class="section-title">安全配置中心</h3>
          <p class="section-subtitle">Gemini 负责结构化文案生成；Etsy 通过 OAuth 完成正式授权，店铺和发布所需 ID 会自动发现或自动创建。</p>
        </div>
        <div class="flex flex-wrap gap-3">
          <button class="btn btn-primary" @click="saveConfig">保存配置</button>
          <button class="btn btn-secondary" @click="loadConfig">重新读取</button>
        </div>
      </div>
    </section>

    <section class="status-grid">
      <article class="status-card">
        <div class="status-card__label">Gemini API Key</div>
        <div class="status-card__value">{{ config.geminiApiKeyConfigured ? '已配置' : '未配置' }}</div>
        <div class="status-card__meta">{{ config.geminiApiKeyMasked || '未写入' }}</div>
      </article>
      <article class="status-card">
        <div class="status-card__label">Etsy App</div>
        <div class="status-card__value">
          {{ config.etsyApiKeyConfigured && config.etsySharedSecretConfigured ? '已配置' : '未配置' }}
        </div>
        <div class="status-card__meta">
          Key：{{ config.etsyApiKeyMasked || '未写入' }} / Secret：{{ config.etsySharedSecretMasked || '未写入' }}
        </div>
      </article>
      <article class="status-card">
        <div class="status-card__label">Etsy OAuth</div>
        <div class="status-card__value">{{ config.etsyConnected ? '已连接' : '未连接' }}</div>
        <div class="status-card__meta">
          {{ config.etsyShopName || config.etsyShopId || '等待授权后自动发现店铺' }}
        </div>
      </article>
    </section>

    <section class="section-shell">
      <div class="section-head">
        <div>
          <div class="section-kicker">Gemini</div>
          <h3 class="section-title">结构化内容生成</h3>
          <p class="section-subtitle">用于生成 Etsy 标题、描述、标签、Alt Text 和建议价格的 JSON 结果。</p>
        </div>
      </div>
      <div class="settings-grid mt-6">
        <div class="settings-card">
          <label class="field-label">Gemini Model</label>
          <input v-model.trim="form.geminiModel" class="field-input" placeholder="gemini-2.5-flash">
          <div class="settings-hint">建议填写：`gemini-2.5-flash`。</div>
        </div>
        <div class="settings-card">
          <label class="field-label">Gemini Endpoint</label>
          <input v-model.trim="form.geminiEndpoint" class="field-input" placeholder="https://...">
          <div class="settings-hint break-all">当前值：{{ config.geminiEndpoint || '-' }}</div>
        </div>
        <div class="settings-card settings-card--wide">
          <label class="field-label">Gemini API Key</label>
          <input v-model.trim="form.geminiApiKey" class="field-input" type="password" placeholder="留空表示保持原值">
          <div class="settings-hint">状态：{{ config.geminiApiKeyConfigured ? `已配置 ${config.geminiApiKeyMasked}` : '未配置' }}</div>
        </div>
      </div>
    </section>

    <section class="section-shell">
      <div class="section-head">
        <div>
          <div class="section-kicker">Etsy OAuth</div>
          <h3 class="section-title">店铺授权与自动发现</h3>
          <p class="section-subtitle">你只需要配置 Etsy 应用凭据并点击授权。Access Token、Refresh Token、Shop ID 都由系统自动获取和续期。</p>
        </div>
        <div class="flex flex-wrap gap-3">
          <button class="btn btn-primary" @click="connectEtsy">连接 Etsy</button>
          <button class="btn btn-secondary" :disabled="!config.etsyConnected" @click="disconnectEtsy">断开连接</button>
        </div>
      </div>
      <div class="settings-grid mt-6">
        <div class="settings-card">
          <label class="field-label">Etsy Endpoint</label>
          <input v-model.trim="form.etsyEndpoint" class="field-input" placeholder="https://api.etsy.com/v3/application">
          <div class="settings-hint break-all">通常保持默认即可。</div>
        </div>
        <div class="settings-card">
          <label class="field-label">Etsy Redirect URI</label>
          <input v-model.trim="form.etsyRedirectUri" class="field-input" placeholder="https://lvyikuan.com/api/integrations/etsy/callback">
          <div class="settings-hint break-all">必须和 Etsy 应用后台登记的回调地址完全一致。当前建议值：`https://lvyikuan.com/api/integrations/etsy/callback`</div>
        </div>
        <div class="settings-card">
          <label class="field-label">Etsy API Key</label>
          <input v-model.trim="form.etsyApiKey" class="field-input" type="password" placeholder="留空表示保持原值">
          <div class="settings-hint">状态：{{ config.etsyApiKeyConfigured ? `已配置 ${config.etsyApiKeyMasked}` : '未配置' }}</div>
        </div>
        <div class="settings-card">
          <label class="field-label">Etsy Shared Secret</label>
          <input v-model.trim="form.etsySharedSecret" class="field-input" type="password" placeholder="留空表示保持原值">
          <div class="settings-hint">状态：{{ config.etsySharedSecretConfigured ? `已配置 ${config.etsySharedSecretMasked}` : '未配置' }}</div>
        </div>
        <div class="settings-card">
          <label class="field-label">已连接店铺</label>
          <input :value="config.etsyShopName || '-'" class="field-input" readonly>
          <div class="settings-hint">Shop ID：{{ config.etsyShopId || '未发现' }}</div>
        </div>
        <div class="settings-card">
          <label class="field-label">Token 到期时间</label>
          <input :value="config.etsyAccessTokenExpiresAt || '系统会自动续期'" class="field-input" readonly>
          <div class="settings-hint">OAuth User ID：{{ config.etsyOauthUserId || '未记录' }}</div>
        </div>
      </div>
    </section>

    <section class="section-shell">
      <div class="section-head">
        <div>
          <div class="section-kicker">Publish Rules</div>
          <h3 class="section-title">自动发布默认项</h3>
          <p class="section-subtitle">这些是业务默认值，不是派生 ID。系统会根据这些规则自动创建 shipping profile、readiness state，并自动匹配 taxonomy。</p>
        </div>
      </div>
      <div class="settings-grid mt-6">
        <div class="settings-card">
          <label class="field-label">默认库存</label>
          <input v-model.number="form.etsyDefaultQuantity" class="field-input" type="number" step="1">
        </div>
        <div class="settings-card">
          <label class="field-label">Who Made</label>
          <select v-model="form.etsyWhoMade" class="field-select">
            <option v-for="item in etsyWhoMadeOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
          </select>
        </div>
        <div class="settings-card">
          <label class="field-label">When Made</label>
          <select v-model="form.etsyWhenMade" class="field-select">
            <option v-for="item in etsyWhenMadeOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
          </select>
        </div>
        <div class="settings-card">
          <label class="field-label">Shipping Profile Title</label>
          <input v-model.trim="form.etsyShippingProfileTitle" class="field-input" placeholder="EpinTong Default Shipping">
          <div class="settings-hint">当前自动解析 ID：{{ config.etsyShippingProfileId || '未生成' }}</div>
        </div>
        <div class="settings-card">
          <label class="field-label">Origin Country ISO</label>
          <input v-model.trim="form.etsyOriginCountryIso" class="field-input" placeholder="US">
        </div>
        <div class="settings-card">
          <label class="field-label">Destination Country ISO</label>
          <input v-model.trim="form.etsyDestinationCountryIso" class="field-input" placeholder="US">
        </div>
        <div class="settings-card">
          <label class="field-label">Destination Region</label>
          <select v-model="form.etsyDestinationRegion" class="field-select">
            <option v-for="item in etsyDestinationRegionOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
          </select>
        </div>
        <div class="settings-card">
          <label class="field-label">Primary Cost</label>
          <input v-model.number="form.etsyPrimaryCost" class="field-input" type="number" step="0.01">
        </div>
        <div class="settings-card">
          <label class="field-label">Secondary Cost</label>
          <input v-model.number="form.etsySecondaryCost" class="field-input" type="number" step="0.01">
        </div>
        <div class="settings-card">
          <label class="field-label">Readiness State</label>
          <select v-model="form.etsyReadinessState" class="field-select">
            <option v-for="item in etsyReadinessStateOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
          </select>
          <div class="settings-hint">当前自动解析 ID：{{ config.etsyReadinessStateId || '未生成' }}</div>
        </div>
        <div class="settings-card">
          <label class="field-label">Min Processing Time</label>
          <input v-model.number="form.etsyMinProcessingTime" class="field-input" type="number" step="1">
        </div>
        <div class="settings-card">
          <label class="field-label">Max Processing Time</label>
          <input v-model.number="form.etsyMaxProcessingTime" class="field-input" type="number" step="1">
        </div>
        <div class="settings-card">
          <label class="field-label">Processing Time Unit</label>
          <select v-model="form.etsyProcessingTimeUnit" class="field-select">
            <option v-for="item in etsyProcessingTimeUnitOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
          </select>
        </div>
        <div class="settings-card">
          <label class="field-label">当前自动匹配 Taxonomy ID</label>
          <input :value="config.etsyTaxonomyId || '按产品内容自动匹配'" class="field-input" readonly>
          <div class="settings-hint">默认情况下不再要求你手工填写 taxonomy id。</div>
        </div>
      </div>
    </section>

    <section class="section-shell">
      <div class="section-head">
        <div>
          <div class="section-kicker">Policies</div>
          <h3 class="section-title">安全约束</h3>
        </div>
      </div>
      <div class="note-grid mt-6">
        <article class="note-card">
          <div class="note-card__title">OAuth 回调</div>
          <p class="note-card__text">Etsy 草稿发布不需要 webhook。完整授权只需要一次 OAuth 回调，系统会保存 refresh token 并自动续期 access token。</p>
        </article>
        <article class="note-card">
          <div class="note-card__title">密钥不回显</div>
          <p class="note-card__text">刷新页面后不会看到 Etsy 或 Gemini 的明文密钥，只会显示掩码和连接状态。</p>
        </article>
        <article class="note-card">
          <div class="note-card__title">派生 ID 自动化</div>
          <p class="note-card__text">店铺、shipping profile、readiness state 和 taxonomy 都会在服务端自动解析或自动创建，不再依赖后台手输这些 ID。</p>
        </article>
      </div>
    </section>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { settingsApi } from '@/api';
import { confirmAction, notifyError, notifyInfo, notifySuccess } from '@/utils/feedback';
import {
  ETSY_DESTINATION_REGION_OPTIONS,
  ETSY_PROCESSING_TIME_UNIT_OPTIONS,
  ETSY_READINESS_STATE_OPTIONS,
  ETSY_WHEN_MADE_OPTIONS,
  ETSY_WHO_MADE_OPTIONS,
  normalizeEtsyWhenMade,
  normalizeEtsyWhoMade
} from '@/constants/etsy';

let popupRef = null;
const etsyWhoMadeOptions = ETSY_WHO_MADE_OPTIONS;
const etsyWhenMadeOptions = ETSY_WHEN_MADE_OPTIONS;
const etsyReadinessStateOptions = ETSY_READINESS_STATE_OPTIONS;
const etsyProcessingTimeUnitOptions = ETSY_PROCESSING_TIME_UNIT_OPTIONS;
const etsyDestinationRegionOptions = ETSY_DESTINATION_REGION_OPTIONS;

const config = ref({
  geminiModel: '',
  geminiEndpoint: '',
  etsyEndpoint: '',
  etsyRedirectUri: '',
  etsyShopId: '',
  etsyShopName: '',
  etsyTaxonomyId: null,
  etsyShippingProfileId: null,
  etsyReadinessStateId: null,
  etsyWhoMade: '',
  etsyWhenMade: '',
  etsyDefaultQuantity: null,
  etsyShippingProfileTitle: '',
  etsyOriginCountryIso: '',
  etsyDestinationCountryIso: '',
  etsyDestinationRegion: '',
  etsyPrimaryCost: null,
  etsySecondaryCost: null,
  etsyReadinessState: '',
  etsyMinProcessingTime: null,
  etsyMaxProcessingTime: null,
  etsyProcessingTimeUnit: '',
  etsyConnected: false,
  etsyOauthUserId: null,
  etsyAccessTokenExpiresAt: '',
  geminiApiKeyConfigured: false,
  geminiApiKeyMasked: '',
  etsyApiKeyConfigured: false,
  etsyApiKeyMasked: '',
  etsySharedSecretConfigured: false,
  etsySharedSecretMasked: '',
  etsyAccessTokenConfigured: false,
  etsyAccessTokenMasked: ''
});

const form = reactive({
  geminiModel: '',
  geminiEndpoint: '',
  geminiApiKey: '',
  etsyEndpoint: '',
  etsyRedirectUri: '',
  etsyWhoMade: '',
  etsyWhenMade: '',
  etsyDefaultQuantity: null,
  etsyShippingProfileTitle: '',
  etsyOriginCountryIso: '',
  etsyDestinationCountryIso: '',
  etsyDestinationRegion: '',
  etsyPrimaryCost: null,
  etsySecondaryCost: null,
  etsyReadinessState: '',
  etsyMinProcessingTime: null,
  etsyMaxProcessingTime: null,
  etsyProcessingTimeUnit: '',
  etsyApiKey: '',
  etsySharedSecret: ''
});

function patchFormFromConfig() {
  form.geminiModel = config.value.geminiModel || '';
  form.geminiEndpoint = config.value.geminiEndpoint || '';
  form.geminiApiKey = '';
  form.etsyEndpoint = config.value.etsyEndpoint || '';
  form.etsyRedirectUri = config.value.etsyRedirectUri || '';
  form.etsyWhoMade = normalizeEtsyWhoMade(config.value.etsyWhoMade);
  form.etsyWhenMade = normalizeEtsyWhenMade(config.value.etsyWhenMade);
  form.etsyDefaultQuantity = config.value.etsyDefaultQuantity ?? null;
  form.etsyShippingProfileTitle = config.value.etsyShippingProfileTitle || '';
  form.etsyOriginCountryIso = config.value.etsyOriginCountryIso || '';
  form.etsyDestinationCountryIso = config.value.etsyDestinationCountryIso || '';
  form.etsyDestinationRegion = config.value.etsyDestinationRegion || 'none';
  form.etsyPrimaryCost = config.value.etsyPrimaryCost ?? null;
  form.etsySecondaryCost = config.value.etsySecondaryCost ?? null;
  form.etsyReadinessState = config.value.etsyReadinessState || 'made_to_order';
  form.etsyMinProcessingTime = config.value.etsyMinProcessingTime ?? null;
  form.etsyMaxProcessingTime = config.value.etsyMaxProcessingTime ?? null;
  form.etsyProcessingTimeUnit = config.value.etsyProcessingTimeUnit || 'days';
  form.etsyApiKey = '';
  form.etsySharedSecret = '';
}

async function loadConfig() {
  try {
    const res = await settingsApi.getAiConfig();
    config.value = res.data;
    patchFormFromConfig();
  } catch (error) {
    notifyError(error.message || '读取配置失败');
  }
}

async function saveConfig() {
  try {
    await settingsApi.saveAiConfig({ ...form });
    notifySuccess('配置已保存，敏感信息已加密');
    await loadConfig();
  } catch (error) {
    notifyError(error.message || '保存配置失败');
  }
}

async function connectEtsy() {
  try {
    await settingsApi.saveAiConfig({ ...form });
    await loadConfig();
    const res = await settingsApi.getEtsyAuthUrl();
    popupRef = window.open(res.data.authorizeUrl, 'etsy-oauth', 'width=760,height=880');
    if (!popupRef) {
      notifyError('浏览器拦截了授权窗口，请允许弹窗后重试');
      return;
    }
    notifyInfo('Etsy 授权窗口已打开，请在新窗口完成登录和授权');
  } catch (error) {
    notifyError(error.message || '打开 Etsy 授权失败');
  }
}

async function disconnectEtsy() {
  if (!(await confirmAction({
    title: '断开 Etsy',
    message: '确认断开 Etsy 授权吗？断开后将清空当前保存的 access token、refresh token 和自动发现的店铺信息。',
    confirmText: '确认断开',
    tone: 'danger'
  }))) {
    return;
  }
  try {
    await settingsApi.disconnectEtsy();
    notifySuccess('Etsy 已断开连接');
    await loadConfig();
  } catch (error) {
    notifyError(error.message || '断开 Etsy 失败');
  }
}

function handleEtsyMessage(event) {
  if (!event.data || typeof event.data !== 'object') {
    return;
  }
  if (event.data.type === 'ETSY_OAUTH_SUCCESS') {
    notifySuccess(event.data.message || 'Etsy 已连接成功');
    loadConfig();
    if (popupRef && !popupRef.closed) {
      popupRef.close();
    }
    popupRef = null;
  }
  if (event.data.type === 'ETSY_OAUTH_FAILED') {
    notifyError(event.data.message || 'Etsy 授权失败');
    if (popupRef && !popupRef.closed) {
      popupRef.close();
    }
    popupRef = null;
  }
}

onMounted(() => {
  loadConfig();
  window.addEventListener('message', handleEtsyMessage);
});

onBeforeUnmount(() => {
  window.removeEventListener('message', handleEtsyMessage);
});
</script>
